import { useEffect, useRef } from "react";

/**
 * Custom React hook for implementing infinite scroll functionality using IntersectionObserver.
 *
 * @param callback - Function to be called when the observed element enters the viewport.
 * @param hasMore - Boolean indicating if there are more items to load.
 * @param loading - Boolean indicating if a loading operation is currently in progress.
 * @returns A ref to be attached to the DOM element that should be observed for infinite scrolling.
 *
 * @example
 * const observerRef = useInfiniteScroll(loadMoreItems, hasMore, loading);
 * return <div ref={observerRef}>Loading...</div>;
 */
export const useInfiniteScroll = (callback: () => void, hasMore: boolean, loading: boolean) => {
  const observerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!hasMore || loading) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        callback();
      }
    }, { threshold: 1 });

    const node = observerRef.current;
    if (node) observer.observe(node);

    return () => {
      if (node) observer.unobserve(node);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasMore, loading]);

  return observerRef;
};